<script lang="ts">
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import { shuffle } from '#lib/util/shuffle.ts';
	import type { AntonioStep } from '#src/types/antonio-step.ts';

	interface Props {
		step: AntonioStep;
		onsubmit: (optionIds: string[]) => void;
	}

	const { step, onsubmit }: Props = $props();

	/**
	 * A form input, not an action row like AntonioButtons: the visitor picks
	 * answers to a question. Single choice submits on tap, same as the fixed
	 * yes/no buttons; multiple choice toggles up to maxSelect and confirms.
	 */
	const multiple = $derived(step.maxSelect > 1);
	const withImages = $derived(step.options.some((option) => option.imageUrl));
	/** Image options are shuffled on every showing, so no picture gets the first slot's advantage. */
	const options = $derived(withImages ? shuffle(step.options) : step.options);

	/** Image options shrink to this before the row turns into a carousel, in px (8rem). */
	const MIN_CARD_WIDTH = 128;
	/** The row's gap, in px (0.5rem). */
	const GAP = 8;

	let containerWidth = $state(0);
	/**
	 * Image options share one row while they all fit at MIN_CARD_WIDTH. Past
	 * that the row swipes, with cards sized so the last visible one is cut in
	 * half: that half card is what tells the visitor there are more.
	 */
	const cardWidth = $derived.by(() => {
		const count = options.length;
		if (
			!withImages ||
			!containerWidth ||
			count * MIN_CARD_WIDTH + (count - 1) * GAP <= containerWidth
		) {
			return undefined;
		}
		const fullCards = Math.max(
			1,
			Math.floor((containerWidth - MIN_CARD_WIDTH / 2) / (MIN_CARD_WIDTH + GAP))
		);
		return (containerWidth - fullCards * GAP) / (fullCards + 0.5);
	});

	let selected: string[] = $state([]);

	function pick(optionId: string) {
		if (!multiple) {
			onsubmit([optionId]);
			return;
		}
		if (selected.includes(optionId)) {
			selected = selected.filter((id) => id !== optionId);
		} else if (selected.length < step.maxSelect) {
			selected = [...selected, optionId];
		}
	}
</script>

<div class="antonio-choice-input">
	<div
		class="options"
		class:with-images={withImages}
		style:--card-width={cardWidth ? `${cardWidth}px` : undefined}
		bind:clientWidth={containerWidth}
	>
		{#each options as option (option.id)}
			{@const isSelected = selected.includes(option.id)}
			<button
				class="option large"
				class:selected={isSelected}
				type="button"
				aria-pressed={multiple ? isSelected : undefined}
				disabled={multiple && !isSelected && selected.length >= step.maxSelect}
				onclick={() => pick(option.id)}
			>
				{#if option.imageUrl}
					<img src={option.imageUrl} alt="" />
				{/if}
				<span class="label">{option.label}</span>
			</button>
		{/each}
	</div>
	{#if multiple}
		<div class="confirm">
			<span class="hint">{t('antonio.choice.hint', { max: step.maxSelect })}</span>
			<button
				class="accent large"
				type="button"
				disabled={selected.length === 0}
				onclick={() => onsubmit(selected)}
			>
				{t('antonio.choice.confirm')}
			</button>
		</div>
	{/if}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.antonio-choice-input {
		@include lib.flex-column();
		gap: 0.5rem;
		padding: 0.5rem 0;

		.options {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
			gap: 0.5rem;

			// One row: shared evenly while all options fit, otherwise swipeable
			// at the card width the script picks.
			&.with-images {
				@include lib.flex-row();
				@include lib.flex-align(flex-start, stretch);
				overflow-x: auto;
				overscroll-behavior-x: contain;
				scroll-snap-type: x mandatory;

				.option {
					@include lib.flex-align(flex-start, stretch);
					flex: 1 0 var(--card-width, 0px);
					scroll-snap-align: start;
					// Global buttons have a fixed height, which the image would overflow.
					height: auto;
					padding: 0.5rem;
					// Image radius plus padding and border, so the corners stay concentric.
					border-radius: 0.875rem;
					// The scroll container would clip a focus ring drawn outside.
					outline-offset: -2px;

					.label {
						white-space: normal;
					}
				}
			}

			.option {
				@include lib.flex-column();
				@include lib.flex-align(center, stretch);
				gap: 0.5rem;
				border: 2px solid transparent;

				img {
					width: 100%;
					aspect-ratio: 1;
					object-fit: cover;
					border-radius: 0.25rem;
				}

				&.selected {
					border-color: var(--accent40);
					background: var(--surface-accent-subtle);
				}

				.label {
					text-align: center;
				}
			}
		}

		.confirm {
			@include lib.flex-row();
			@include lib.flex-align(space-between, center);
			gap: 0.5rem;

			.hint {
				font-size: 0.875rem;
				color: var(--text-secondary);
			}
		}
	}
</style>
